import { useCallback } from "react";
import { useLockStore } from "../store/lockStore";
import { lockApi } from "../api/lockApi";
import { saveCurrentDraft } from "../services/saveDraft";
import { asError, eventBus } from "@optimyl/events";

const REQUEST_TIMEOUT_SECONDS = 30;

interface UseLockActionsOptions {
  /**
   * Optional refetch-form-data callback. When passed, `requestAccess` falls
   * back to acquiring the lock directly if the server reports no lock exists
   * (lock TTL'd between the user arriving on the lock screen and clicking
   * the button). On successful fallback acquire, the reload runs before
   * flipping to `locked` so a submitted-while-they-waited form routes to
   * StepThankYou cleanly. Same callback FormPage passes to useLockManager.
   */
  onLockGranted?: () => Promise<void>;
}

/**
 * Bound user-action callbacks that the lock UI components fire on button click.
 * Reads sessionId / lockedBy from {@link useLockStore} so callers don't pass them.
 *
 * Action semantics:
 *  - requestAccess  — caller is on the lock screen, asking the holder for access
 *  - cancelRequest  — caller withdraws an outgoing request
 *  - grant          — lock holder grants the incoming request (saves form first)
 *  - deny           — lock holder denies the incoming request
 */
export function useLockActions(formId: string | null, options?: UseLockActionsOptions) {
  const sessionId = useLockStore((s) => s.sessionId);
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setCountdown = useLockStore((s) => s.setCountdown);
  const setOutgoingStatus = useLockStore((s) => s.setOutgoingStatus);
  const setIncomingRequest = useLockStore((s) => s.setIncomingRequest);
  const setRequestInProgress = useLockStore((s) => s.setRequestInProgress);
  const lockedBy = useLockStore((s) => s.lockedBy);
  const onLockGranted = options?.onLockGranted;

  const requestAccess = useCallback(async () => {
    if (!formId) return;
    try {
      const result = await lockApi.requestAccess(formId, sessionId);
      if (result.submitted) {
        setOutgoingStatus("Pending");
        setLockStatus("requesting", lockedBy);
        setCountdown(REQUEST_TIMEOUT_SECONDS);
        return;
      }
      // submitted=false has two server-side meanings:
      //   (a) another user already has a Pending request — alreadyRequestingBy is set.
      //       UI already shows that in `requestInProgress`; no further action needed.
      //   (b) the lock doesn't exist (it TTL'd between the user arriving on the
      //       lock screen and clicking the button) — alreadyRequestingBy is empty.
      //       Without a fallback the click looks like a no-op. Recover by acquiring
      //       directly and running the same refetch-then-flip dance as the
      //       lockReleased recovery path so a submitted-while-they-waited form
      //       routes to StepThankYou.
      if (!result.alreadyRequestingBy) {
        const reloadPromise = onLockGranted
          ? onLockGranted().catch((err) => {
              eventBus.publish("error", { error: asError(err), context: "useLockActions.requestAccess.reloadFallback" });
            })
          : Promise.resolve();
        const acquireResult = await lockApi.acquireLock(formId, sessionId);
        if (acquireResult.acquired) {
          await reloadPromise;
          setLockStatus("locked");
        } else if (acquireResult.lockedBy && acquireResult.lockedBy !== lockedBy) {
          // Lock has a new holder we hadn't heard about yet; refresh the
          // displayed email so the user knows who actually has it.
          setLockStatus("locked-by-other", acquireResult.lockedBy);
        }
      }
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useLockActions.requestAccess" });
    }
  }, [formId, sessionId, lockedBy, onLockGranted, setLockStatus, setCountdown, setOutgoingStatus]);

  const reclaim = useCallback(async () => {
    // Direct acquire — used when the new holder is the SAME user (different
    // device/tab). Bypasses the Request Access flow: that flow would file a
    // Pending request, which makes no sense when it'd be against yourself, and
    // would force a 30s wait before auto-grant. With Path 4 of AcquireLockScript
    // unconditional for same-email, this acquire returns acquired=true on the
    // first try and the abandoned holder takes the lock back immediately.
    if (!formId) return;
    try {
      const reloadPromise = onLockGranted
        ? onLockGranted().catch((err) => {
            eventBus.publish("error", { error: asError(err), context: "useLockActions.reclaim.reload" });
          })
        : Promise.resolve();
      const result = await lockApi.acquireLock(formId, sessionId);
      if (result.acquired) {
        await reloadPromise;
        setLockStatus("locked");
      } else if (result.lockedBy) {
        // Someone else (a different email) raced and grabbed it in the gap.
        // Fall back to showing the lock screen so they can Request Access.
        setLockStatus("locked-by-other", result.lockedBy);
      }
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useLockActions.reclaim" });
    }
  }, [formId, sessionId, onLockGranted, setLockStatus]);

  const cancelRequest = useCallback(async () => {
    if (!formId) return;
    // Flip the local UI immediately — the server call is fire-and-forget for UX
    // responsiveness. The server clears the pending request and pushes
    // requestResolved {Cancelled} to the group so the holder's banner disappears.
    // requestInProgress also needs an immediate local clear: the broadcast of our
    // own accessRequested back to us set it, and waiting for the round-trip
    // requestResolved push to clear it would flash a yellow "[you] is requesting
    // access" line on the lock screen for ~200ms.
    setLockStatus("locked-by-other", lockedBy);
    setOutgoingStatus(null);
    setCountdown(null);
    setRequestInProgress(null);
    try {
      await lockApi.cancelRequest(formId);
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useLockActions.cancelRequest" });
    }
  }, [formId, lockedBy, setLockStatus, setOutgoingStatus, setCountdown, setRequestInProgress]);

  const grant = useCallback(async () => {
    if (!formId) return;
    try {
      // Save the form first so the requester sees the latest edits when they take over.
      // If the save fails, we still grant the lock — better to risk a stale-data edit than
      // to block the requester over a transient SF issue.
      await saveCurrentDraft().catch((err) => {
        eventBus.publish("error", { error: asError(err), context: "useLockActions.grant.saveBeforeGrant" });
      });
      await lockApi.respondToRequest(formId, sessionId, "Grant");
      // The SignalR push will drive the actual state transition in useLockManager.
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useLockActions.grant" });
    }
  }, [formId, sessionId]);

  const deny = useCallback(async () => {
    if (!formId) return;
    try {
      await lockApi.respondToRequest(formId, sessionId, "Deny");
      // Optimistic clear — useLockManager's SignalR handler also clears these but
      // doing it here makes the banner disappear instantly on the holder's screen.
      setIncomingRequest(null);
      setCountdown(null);
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "useLockActions.deny" });
    }
  }, [formId, sessionId, setIncomingRequest, setCountdown]);

  return { requestAccess, reclaim, cancelRequest, grant, deny };
}
